import type { CSSProperties, MaybeRefOrGetter, ShallowRef } from 'vue';
import { computed, toValue } from 'vue';

type UseBidirectionalStickyOptions = {
    enabled?: MaybeRefOrGetter<boolean>;
    topOffset?: MaybeRefOrGetter<number>;
    bottomOffset?: MaybeRefOrGetter<number>;
    elementRef?: Readonly<ShallowRef<HTMLElement | null>>;
    throttle?: number;
    box?: 'content-box' | 'border-box';
    transition?: string;
};

/**
 * Creates a bidirectional sticky style for an element, clamping the element to the top or
 * bottom of the viewport depending on the scroll direction.
 *
 * @param options - The options for the useBidirectionalSticky composable.
 * @returns The sticky style for the element.
 *
 * @example
 * ```ts
 * const { stickyStyle } = useBidirectionalSticky({
 *     topOffset: 160,
 *     bottomOffset: 16,
 *     elementRef: ref<HTMLElement | null>(null),
 *     throttle: 25,
 *     box: 'content-box',
 *     transition: 'top 0.3s ease-in-out'
 * });
 * ```
 */
export function useBidirectionalSticky(options: UseBidirectionalStickyOptions = {}) {
    const enabled = computed(() => toValue(options.enabled ?? true));
    const topOffset = computed(() => toValue(options.topOffset ?? 0));
    const bottomOffset = computed(() => toValue(options.bottomOffset ?? 0));
    const throttle = options.throttle ?? 25;
    const box = options.box ?? 'border-box';
    const transition = options.transition ?? 'top 0.3s ease-in-out';

    const lastScrollDirection = ref<'up' | 'down' | null>(null);

    const { directions } = useWindowScroll({
        throttle: throttle,
        onScroll: handleScroll
    });

    const element = computed(() => toValue(options.elementRef));
    const { height: heightRef } = useElementSize(element, undefined, { box: box });
    function handleScroll() {
        lastScrollDirection.value = directions.top ? 'up' : 'down';
    }

    const stickyStyle = computed<CSSProperties | undefined>(() => {
        if (!enabled.value) {
            return undefined;
        }

        const elementHeight = heightRef.value;

        if (lastScrollDirection.value === 'down') {
            if (import.meta.client && elementHeight && elementHeight > window.innerHeight) {
                return {
                    transition: transition,
                    position: 'sticky',
                    top: `calc(100dvh - ${elementHeight}px - ${bottomOffset.value}px)`
                };
            }
        }

        return {
            transition: transition,
            position: 'sticky',
            top: `${topOffset.value}px`
        };
    });

    return { stickyStyle };
}
